import { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Download, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar
} from 'recharts';
import { toast } from "sonner";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const AnalyticsDashboard = () => {
    const { data: stats, isLoading } = trpc.analytics.getPersonalStats.useQuery();
    const generateReport = trpc.analytics.generateReport.useMutation({
        onSuccess: (data) => {
            toast.success("Report generated successfully");
            // In a real app, this would trigger a download
            console.log("Download URL:", data.downloadUrl);
            window.open(data.downloadUrl, '_blank');
        },
        onError: (err) => {
            toast.error("Failed to generate report: " + err.message);
        }
    });

    const handleDownload = (format: 'PDF' | 'CSV') => {
        generateReport.mutate({ format });
    };

    if (isLoading) {
        return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    }

    if (!stats) {
        return <div className="text-center p-12 text-muted-foreground">No analytics data available.</div>;
    }

    // Transform data for charts
    // Group records by date for the chart
    const chartData = stats.records.reduce((acc: any[], record: any) => {
        const date = new Date(record.timestamp).toLocaleDateString();
        const existing = acc.find(item => item.date === date);
        const amount = Number(record.amount);

        if (existing) {
            if (record.type === 'EARNING') existing.earnings += amount;
            else if (record.type === 'PAYMENT' || record.type === 'FEE') existing.expenses += amount;
        } else {
            acc.push({
                date,
                earnings: record.type === 'EARNING' ? amount : 0,
                expenses: (record.type === 'PAYMENT' || record.type === 'FEE') ? amount : 0
            });
        }
        return acc;
    }, []).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Financial Analytics</h2>
                    <p className="text-muted-foreground">immutable records of your platform activity.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleDownload('CSV')} disabled={generateReport.isLoading}>
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                    </Button>
                    <Button variant="default" size="sm" onClick={() => handleDownload('PDF')} disabled={generateReport.isLoading}>
                        <Download className="w-4 h-4 mr-2" />
                        Export PDF
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
                        <TrendingUp className="h-4 w-4 text-green-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-500">
                            MWK {stats.summary.totalEarned.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Lifetime earnings</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
                        <TrendingDown className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-500">
                            MWK {stats.summary.totalSpent.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Services & Fees</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Net Value</CardTitle>
                        <DollarSign className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            MWK {(stats.summary.totalEarned - stats.summary.totalSpent).toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Balance flow</p>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>Activity Overview</CardTitle>
                        <CardDescription>Daily financial movement</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" fontSize={12} />
                                <YAxis fontSize={12} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: 'hsl(var(--card))', borderRadius: '8px', border: '1px solid hsl(var(--border))' }}
                                />
                                <Bar dataKey="earnings" name="Earnings" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Recent Transactions Table */}
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>Recent Records</CardTitle>
                        <CardDescription>Immutable ledger entries</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {stats.records.slice(0, 5).map((record: any) => (
                                    <TableRow key={record.id}>
                                        <TableCell className="font-medium bg-transparent">
                                            {new Date(record.timestamp).toLocaleDateString()}
                                            <div className="text-xs text-muted-foreground">
                                                {new Date(record.timestamp).toLocaleTimeString()}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={record.type === 'EARNING' ? 'default' : 'outline'} className={record.type === 'EARNING' ? 'bg-green-500 hover:bg-green-600' : ''}>
                                                {record.type}
                                            </Badge>
                                            <div className="text-xs text-muted-foreground mt-1">{record.category}</div>
                                        </TableCell>
                                        <TableCell className={`text-right font-bold ${record.type === 'EARNING' ? 'text-green-500' :
                                                record.type === 'PAYMENT' || record.type === 'FEE' ? 'text-red-500' : ''
                                            }`}>
                                            {record.type === 'EARNING' ? '+' : '-'}
                                            MWK {Number(record.amount).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {stats.records.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                                            No records found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
